import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { csrfService } from './csrf.service';

/**
 * API Client wrapper
 * Handles JWT token injection, error formatting, and response parsing
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // CRITICAL: No fallback to localhost - fail fast if config is missing
    const apiUrl = import.meta.env.VITE_API_URL;
    
    if (!apiUrl) {
      throw new Error(
        '❌ VITE_API_URL is not set!\n\n' +
        'This application requires VITE_API_URL to be set at build time.\n\n' +
        'Local development:\n' +
        '  1. Copy apps/admin/.env.example to apps/admin/.env.local\n' +
        '  2. Set VITE_API_URL=/api (uses Vite proxy)\n' +
        '  3. Restart: npm run dev\n\n' +
        'Deployment:\n' +
        '  - CI/CD: Set GitHub environment variable VITE_API_URL\n' +
        '  - Manual: export VITE_API_URL=https://api-staging.ministryofwine.co.nz\n\n' +
        'See: docs/canonical/technical/environment-configuration.md'
      );
    }
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 10000,
      withCredentials: true, // Required to send/receive cookies in cross-origin requests
    });

    // Add token to requests
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token to state-changing requests
        const method = config.method?.toUpperCase();
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method || '')) {
          const csrfToken = csrfService.getToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Handle responses and errors
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 403 - could be CSRF token issue, try refreshing once
        if (error.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await csrfService.fetchToken();
            const csrfToken = csrfService.getToken();
            if (csrfToken && originalRequest.headers) {
              originalRequest.headers['X-CSRF-Token'] = csrfToken;
            }
            return this.client.request(originalRequest);
          } catch (refreshError) {
            console.error('Failed to refresh CSRF token:', refreshError);
            // Fall through to normal error handling
          }
        }

        // Handle 401 - token expired (but not on login page where we want to show errors)
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
          localStorage.removeItem('token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }

        // Return error in standard format
        const errorData = (error.response?.data as any) || {
          error: { message: error.message },
        };
        return Promise.reject(errorData);
      },
    );
  }

  get<T = unknown>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
    return this.client.get(url, config);
  }

  post<T = unknown, B = unknown>(
    url: string,
    data?: B,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.client.post(url, data, config);
  }

  put<T = unknown, B = unknown>(
    url: string,
    data?: B,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.client.put(url, data, config);
  }

  patch<T = unknown, B = unknown>(
    url: string,
    data?: B,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.client.patch(url, data, config);
  }

  delete<T = unknown>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
    return this.client.delete(url, config);
  }

  /**
   * Raw GET returning the full AxiosResponse (headers + data).
   * Used for blob downloads where Content-Disposition filename is needed.
   */
  async getWithHeaders<T = unknown>(url: string, config: AxiosRequestConfig = {}) {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const response = await axios.get<T>(
      `${this.client.defaults.baseURL}${url}`,
      {
        ...config,
        withCredentials: true, // Required for cookies
        headers: {
          ...config.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    return { data: response.data, headers: response.headers };
  }

  /**
   * Raw POST returning the full AxiosResponse (headers + data).
   */
  async postWithHeaders<T = unknown, B = unknown>(url: string, data?: B, config: AxiosRequestConfig = {}) {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    let csrfToken = csrfService.getToken();
    if (!csrfToken) {
      await csrfService.fetchToken();
      csrfToken = csrfService.getToken();
    }
    const response = await axios.post<T>(
      `${this.client.defaults.baseURL}${url}`,
      data,
      {
        ...config,
        withCredentials: true, // send the _csrf cookie for double-submit CSRF check
        headers: {
          ...config.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      },
    );
    return { data: response.data, headers: response.headers };
  }
}

export const apiClient = new ApiClient();
